/**
 * OBD II Feature-växling för vald enhet (Teltonika 40000) + anti-larm + Disable.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { BoltIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import api from '@/services/api/client';
import { useAuth } from '@/store/authStore';
import { useTranslation } from '@/store/languageStore';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';
import { isSysAdminUser } from '@/utils/isSysAdminUser';

type ObdFeatureMode = 'obd_disable' | 'obd_auto' | 'eld_heavy' | 'non_obd_oem' | 'anti_alarm';

interface ObdCommandResult {
  command: string;
  response: string | null;
  ok: boolean;
  queued?: boolean;
}

interface ApplyObdModeResponse {
  success: boolean;
  message?: string;
  data?: {
    allOk: boolean;
    queued?: boolean;
    commands: ObdCommandResult[];
    summary: string;
  };
  error?: string;
}

interface ObdDisableLogRow {
  id: number;
  imei: string;
  registration_number: string | null;
  delivery_status: 'sent' | 'queued' | 'failed';
  sent_by_username: string | null;
  created_at: string;
}

interface DeviceObdModePanelProps {
  imei: string;
  registrationNumber?: string | null;
}

function confirmMessageKey(mode: ObdFeatureMode): string {
  switch (mode) {
    case 'obd_disable':
      return 'deviceManagement.obdModeConfirmDisable';
    case 'obd_auto':
      return 'deviceManagement.obdModeConfirmObdAuto';
    case 'eld_heavy':
      return 'deviceManagement.obdModeConfirmEld';
    case 'non_obd_oem':
      return 'deviceManagement.obdModeConfirmNonObd';
    case 'anti_alarm':
      return 'deviceManagement.obdModeConfirmAntiAlarm';
    default: {
      const _exhaustive: never = mode;
      return _exhaustive;
    }
  }
}

function formatLocal(iso: string): string {
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'short',
      timeStyle: 'short',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

const DeviceObdModePanel: React.FC<DeviceObdModePanelProps> = ({ imei, registrationNumber }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { confirm, ConfirmDialog } = useConfirmDialog();
  const canUseObdTools = isSysAdminUser(user);
  const [loadingMode, setLoadingMode] = useState<ObdFeatureMode | null>(null);
  const [latestDisable, setLatestDisable] = useState<ObdDisableLogRow | null>(null);
  const [recentDisables, setRecentDisables] = useState<ObdDisableLogRow[]>([]);
  const [showLog, setShowLog] = useState(false);

  const refreshDisableInfo = useCallback(async () => {
    if (!canUseObdTools) return;
    try {
      const [latestRes, listRes] = await Promise.all([
        api.get<{ success: boolean; data: ObdDisableLogRow | null }>(
          `/api/device-management/devices/${encodeURIComponent(imei)}/obd-disable-latest`,
        ),
        api.get<{ success: boolean; data: ObdDisableLogRow[] }>(
          '/api/device-management/obd-disable-log?limit=30',
        ),
      ]);
      setLatestDisable(latestRes.data?.data ?? null);
      setRecentDisables(listRes.data?.data ?? []);
    } catch {
      // Logg är kompletterande — tysta fel
    }
  }, [imei, canUseObdTools]);

  useEffect(() => {
    void refreshDisableInfo();
  }, [refreshDisableInfo]);

  if (!canUseObdTools) {
    return null;
  }

  const runMode = async (mode: ObdFeatureMode) => {
    setLoadingMode(mode);
    try {
      const res = await api.post<ApplyObdModeResponse>(
        `/api/device-management/devices/${encodeURIComponent(imei)}/obd-mode`,
        { mode },
      );
      const payload = res.data;
      if (payload?.success) {
        toast.success(payload.message || t('deviceManagement.obdModeSuccess'));
      } else if (payload?.data) {
        toast.error(payload.message || t('deviceManagement.obdModePartialFail'));
      } else {
        toast.error(payload?.error || t('deviceManagement.obdModeError'));
      }
      if (mode === 'obd_disable') {
        void refreshDisableInfo();
      }
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string } }; message?: string };
      toast.error(e?.response?.data?.error || e?.message || t('deviceManagement.obdModeError'));
    } finally {
      setLoadingMode(null);
    }
  };

  const askAndRun = (mode: ObdFeatureMode) => {
    confirm({
      title: t('deviceManagement.obdModeConfirmTitle'),
      message: t(confirmMessageKey(mode)).replace('{reg}', registrationNumber || imei),
      onConfirm: () => void runMode(mode),
      variant:
        mode === 'non_obd_oem' ||
        mode === 'eld_heavy' ||
        mode === 'anti_alarm' ||
        mode === 'obd_disable'
          ? 'warning'
          : 'info',
    });
  };

  return (
    <div className="mb-6">
      <h3 className="text-lg font-semibold text-textDefault mb-1 flex items-center gap-2">
        <BoltIcon className="w-5 h-5" />
        {t('deviceManagement.obdModeTitle')}
      </h3>
      <p className="text-sm text-textSecondary mb-3">{t('deviceManagement.obdModeHint')}</p>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={loadingMode != null}
          onClick={() => askAndRun('obd_auto')}
          className="flex items-center gap-2 px-3 py-2 bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300 rounded-lg hover:bg-sky-200 dark:hover:bg-sky-900/50 transition-colors disabled:opacity-50"
        >
          {loadingMode === 'obd_auto' ? '…' : t('deviceManagement.obdModeAuto')}
        </button>
        <button
          type="button"
          disabled={loadingMode != null}
          onClick={() => askAndRun('eld_heavy')}
          className="flex items-center gap-2 px-3 py-2 bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 rounded-lg hover:bg-amber-200 dark:hover:bg-amber-900/50 transition-colors disabled:opacity-50"
        >
          {loadingMode === 'eld_heavy' ? '…' : t('deviceManagement.obdModeEld')}
        </button>
        <button
          type="button"
          disabled={loadingMode != null}
          onClick={() => askAndRun('non_obd_oem')}
          className="flex items-center gap-2 px-3 py-2 bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 rounded-lg hover:bg-violet-200 dark:hover:bg-violet-900/50 transition-colors disabled:opacity-50"
        >
          {loadingMode === 'non_obd_oem' ? '…' : t('deviceManagement.obdModeNonObd')}
        </button>
        <button
          type="button"
          disabled={loadingMode != null}
          onClick={() => askAndRun('anti_alarm')}
          className="flex items-center gap-2 px-3 py-2 bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300 rounded-lg hover:bg-rose-200 dark:hover:bg-rose-900/50 transition-colors disabled:opacity-50"
        >
          {loadingMode === 'anti_alarm' ? '…' : t('deviceManagement.obdModeAntiAlarm')}
        </button>
        <button
          type="button"
          disabled={loadingMode != null}
          onClick={() => askAndRun('obd_disable')}
          className="flex items-center gap-2 px-3 py-2 bg-slate-200 dark:bg-slate-700/50 text-slate-800 dark:text-slate-200 rounded-lg hover:bg-slate-300 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
        >
          {loadingMode === 'obd_disable' ? '…' : t('deviceManagement.obdModeDisable')}
        </button>
      </div>

      {latestDisable && (
        <p className="mt-2 text-xs text-textSecondary">
          {t('deviceManagement.obdDisableLastSent')
            .replace('{when}', formatLocal(String(latestDisable.created_at)))
            .replace('{status}', latestDisable.delivery_status)
            .replace('{user}', latestDisable.sent_by_username || '—')}
        </p>
      )}

      <div className="mt-3">
        <button
          type="button"
          onClick={() => setShowLog((v) => !v)}
          className="text-xs text-primary hover:underline"
        >
          {showLog
            ? t('deviceManagement.obdDisableLogHide')
            : t('deviceManagement.obdDisableLogShow')}
        </button>
        {showLog && (
          <div className="mt-2 max-h-48 overflow-auto rounded border border-border bg-bgLight/40 text-xs">
            {recentDisables.length === 0 ? (
              <p className="p-2 text-textSecondary">{t('deviceManagement.obdDisableLogEmpty')}</p>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="text-left text-textSecondary border-b border-border">
                    <th className="px-2 py-1">{t('deviceManagement.obdDisableLogWhen')}</th>
                    <th className="px-2 py-1">{t('deviceManagement.obdDisableLogDevice')}</th>
                    <th className="px-2 py-1">{t('deviceManagement.obdDisableLogStatus')}</th>
                    <th className="px-2 py-1">{t('deviceManagement.obdDisableLogBy')}</th>
                  </tr>
                </thead>
                <tbody>
                  {recentDisables.map((row) => (
                    <tr
                      key={row.id}
                      className={
                        row.imei === imei
                          ? 'bg-slate-100 dark:bg-slate-800/40 text-textDefault'
                          : 'text-textSecondary'
                      }
                    >
                      <td className="px-2 py-1 whitespace-nowrap">
                        {formatLocal(String(row.created_at))}
                      </td>
                      <td className="px-2 py-1 font-mono">
                        {row.registration_number || row.imei}
                      </td>
                      <td className="px-2 py-1">{row.delivery_status}</td>
                      <td className="px-2 py-1">{row.sent_by_username || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
      <ConfirmDialog />
    </div>
  );
};

export default DeviceObdModePanel;
